import { Caption } from '../types';
import { getApiUrl } from '../config/api';

/**
 * Separate captions and titles from a mixed array
 */
export const separateCaptionsAndTitles = (items: Caption[]): {
  captions: Caption[];
  titles: Caption[];
} => {
  const captions = items
    .filter(item => item.itemType === 'captions' || !item.itemType)
    .map((caption, index) => ({
      ...caption,
      id: (index + 1).toString()
    }));
  
  const titles = items
    .filter(item => item.itemType === 'titles')
    .map((title, index) => ({
      ...title,
      id: (index + 1).toString()
    }));
  
  return { captions, titles };
};

/**
 * Combine captions and titles with proper itemType tags
 */
export const combineCaptionsAndTitles = (
  captions: Caption[],
  titles: Caption[]
): Caption[] => {
  const captionsWithType = captions.map(item => ({
    ...item,
    itemType: 'captions' as const
  }));
  
  const titlesWithType = titles.map(item => ({
    ...item,
    itemType: 'titles' as const
  }));
  
  return [...captionsWithType, ...titlesWithType];
};

/**
 * Refetch captions with word count parameter
 */
export const refetchCaptionsWithWordCount = async (
  projectId: string,
  maxWords: number,
  token?: string
): Promise<Caption[]> => {
  const headers: Record<string, string> = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(
    getApiUrl(`/video/get-captions/${projectId}?maxWords=${maxWords}`),
    {
      method: 'GET',
      headers,
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to refetch captions');
  }
  
  const reformattedCaptions = await response.json();
  
  return reformattedCaptions.map((caption: any, index: number) => ({
    ...caption,
    id: (index + 1).toString(),
    itemType: 'captions'
  }));
};

/**
 * Get word count recommendation text
 */
export const getWordCountRecommendation = (wordCount: number): string => {
  if (wordCount === 1) return '📱 Perfect for vertical video (9:16)';
  if (wordCount >= 2 && wordCount <= 5) return '⚡ Great for TikTok/Reels';
  if (wordCount >= 6 && wordCount <= 10) return '📺 Good for social media';
  if (wordCount >= 11 && wordCount <= 15) return '🎬 Standard YouTube captions';
  if (wordCount >= 16) return '📖 Longer, detailed captions';
  return '';
};

/**
 * Apply drag position to caption(s)
 */
export const applyCaptionDrag = (
  items: Caption[],
  captionId: string,
  newYPosition: number,
  newXPosition: number | undefined,
  applyToAll: boolean
): Caption[] => {
  if (applyToAll) {
    // Apply to all captions
    return items.map(item => ({
      ...item,
      style: {
        ...item.style,
        yPosition: newYPosition,
        ...(newXPosition !== undefined && { xPosition: newXPosition })
      }
    }));
  } else {
    // Apply to single caption
    return items.map(item =>
      item.id === captionId
        ? {
            ...item,
            style: {
              ...item.style,
              yPosition: newYPosition,
              ...(newXPosition !== undefined && { xPosition: newXPosition })
            }
          }
        : item
    );
  }
};
